// Tela de Retiradas Pessoais (issue #17), com o cadastro de Responsável num
// modal — o mesmo padrão de "Gerenciar categorias".
//
// A tela existe para uma coisa: manter as retiradas **separadas** das despesas
// da empresa (ADR-0011). Por isso ela não some com Contas a Pagar em nenhum
// lugar, e o total do mês aparece sozinho no topo.

import { newId } from "../../domain/ids.ts";
import { formatData, hojeISO } from "../../domain/datas.ts";
import { formatReais } from "../../domain/money.ts";
import {
  mesDe,
  situacaoLimite,
  totalRetiradoNoMes,
  type Responsavel,
  type RetiradaPessoal,
} from "../../domain/retiradas.ts";
import type { Store } from "../store.ts";
import { clear, el, openModal, tabelaPaginada, botao } from "../ui.ts";
import {
  abrirConfirmacao,
  abrirFormModal,
  barraCadastro,
  campo,
  inputData,
  inputTexto,
  select,
  telaLista,
} from "./_cadastro.ts";
import { criarAcaoComando } from "./_acao.ts";
import { toastErro } from "../toast.ts";
import { inputMoeda } from "./_input-moeda.ts";
import { EstadoLista } from "./_estado-lista.ts";
import { buscaDaLista, filtroMultiplo } from "./_lista-filtros.ts";
import { podeEditarModulo } from "./_permissao.ts";

/** Os meses que têm retirada, do mais recente para o mais antigo. */
function mesesComRetirada(retiradas: RetiradaPessoal[]): string[] {
  return [...new Set(retiradas.map((r) => mesDe(r.data)))].sort((a, b) =>
    b.localeCompare(a),
  );
}

function rotuloMes(mes: string): string {
  const [ano, m] = mes.split("-");
  const nomes = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
  ];
  return `${nomes[Number(m) - 1] ?? m}/${ano}`;
}

export function renderRetiradas(container: HTMLElement, store: Store): () => void {
  // filtros, busca e página num estado só (#93): null = todos
  const filtros = new EstadoLista({ responsavel: null as string[] | null, mes: null as string[] | null });
  const busca = buscaDaLista(filtros, "Buscar por responsável…", () => render());
  const fResponsavel = filtroMultiplo(filtros, "responsavel", "Responsável", () => render());
  const fMes = filtroMultiplo(filtros, "mes", "Mês", () => render());

  const render = (): void => {
    clear(container);
    const snap = store.get();
    const podeGravar = podeEditarModulo(store, "financeiro");
    const respNome = new Map(snap.responsaveis.map((r) => [r.id, r.nome]));

    const meses = mesesComRetirada(snap.retiradasPessoais);

    fResponsavel.atualizar(
      [...snap.responsaveis]
        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))
        .map((r) => ({ id: r.id, nome: r.nome })),
    );
    fMes.atualizar(meses.map((m) => ({ id: m, nome: rotuloMes(m) })));
    const filtroResp = fResponsavel.selecionados();
    const filtroMes = fMes.selecionados();
    const lista = snap.retiradasPessoais
      .filter((r) => filtroResp.size === 0 || filtroResp.has(r.responsavelId))
      .filter((r) => filtroMes.size === 0 || filtroMes.has(mesDe(r.data)))
      .filter((r) => busca.casa(respNome.get(r.responsavelId)))
      .sort((a, b) => b.data.localeCompare(a.data));

    const gerir = botao("secundario", {}, "Responsáveis");
    gerir.addEventListener("click", () => abrirGerenciarResponsaveis(store, render));

    const barra = barraCadastro(
      [fResponsavel.element, fMes.element],
      "Nova Retirada",
      () => abrirModal(null),
      () => {
        filtros.limpar();
        render();
      },
      podeGravar ? [gerir] : [],
      podeGravar,
    );

    // Resumo do mês: o total à parte, e o limite de cada Responsável.
    const resumo = el("div", { class: "retiradas-resumo" });
    const mesesSel = filtroMes.size === 0 ? meses : meses.filter((m) => filtroMes.has(m));
    for (const mes of mesesSel) {
      resumo.append(
        el(
          "p",
          {},
          el("b", {}, `Total retirado em ${rotuloMes(mes)}: `),
          formatReais(totalRetiradoNoMes(snap, mes)),
        ),
      );
      const comLimite = snap.responsaveis
        .filter((r) => r.ativo)
        .map((r) => ({ r, sit: situacaoLimite(snap, r.id, mes) }))
        .filter(({ sit }) => sit.limite != null || sit.retirado > 0);
      for (const { r, sit } of comLimite) {
        resumo.append(
          el(
            "p",
            { class: sit.estourou ? "form-erro" : "muted" },
            `${r.nome}: ${formatReais(sit.retirado)}` +
              (sit.limite == null
                ? " (sem limite)"
                : ` de ${formatReais(sit.limite)}${sit.estourou ? " — acima do limite" : ""}`),
          ),
        );
      }
    }

    if (lista.length === 0) {
      container.append(
        telaLista(
          barra,
          busca.elemento(),
          resumo,
          el(
            "p",
            { class: "muted lista-vazia" },
            snap.responsaveis.length === 0
              ? "Cadastre um responsável antes de registrar retiradas."
              : filtros.mensagemVazio({ nenhum: "Nenhuma retirada.", filtrado: "Nenhuma retirada com esses filtros." }),
          ),
        ),
      );
      return;
    }

    const linhas = lista.map((r) => {
      const acoes: Node[] = [];
      if (podeGravar) {
        const editar = botao("secundario", {}, "Editar");
        editar.addEventListener("click", () => abrirModal(r));
        const excluir = botao("perigo", {}, "Excluir");
        excluir.addEventListener("click", () => {
          abrirConfirmacao({
            titulo: "Excluir esta Retirada?",
            mensagem: `A retirada de ${formatReais(r.valorCentavos)} de ${
              respNome.get(r.responsavelId) ?? "—"
            } sai do sistema. Não dá para desfazer.`,
            rotuloConfirmar: "Excluir",
            perigo: true,
            aoConfirmar: () =>
              void store.executar(
                { agregado: "retirada", cmd: { tipo: "excluir", id: r.id } },
                "Retirada excluída.",
              ),
          });
        });
        acoes.push(editar, excluir);
      }
      return el(
        "tr",
        {},
        el("td", { class: "muted", "data-sort": r.data }, formatData(r.data)),
        el("td", {}, respNome.get(r.responsavelId) ?? "—"),
        el(
          "td",
          { class: "num", "data-sort": String(r.valorCentavos) },
          formatReais(r.valorCentavos),
        ),
        el("td", { class: "acoes" }, ...acoes),
      );
    });

    container.append(
      telaLista(
        barra,
        busca.elemento(),
        resumo,
        tabelaPaginada(
          "retiradas",
          [
            { t: "Data", w: 130 },
            "Responsável",
            { t: "Valor", w: 130, ord: true as const },
            { t: "", ord: false as const },
          ],
          linhas,
          filtros.tabela,
          render,
        ),
      ),
    );
  };

  const abrirModal = (atual: RetiradaPessoal | null): void => {
    const snap = store.get();
    const ativos = [...snap.responsaveis]
      .filter((r) => r.ativo || r.id === atual?.responsavelId)
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
    if (ativos.length === 0) {
      toastErro("Cadastre um responsável antes de registrar retiradas.");
      return;
    }
    const responsavel = select(
      ativos.map((r) => [r.id, r.nome] as const),
      atual?.responsavelId ?? ativos[0]!.id,
    );
    const data = inputData(atual?.data ?? hojeISO());
    const valor = inputMoeda({
      centavos: atual?.valorCentavos ?? null,
      ariaLabel: "Valor da retirada",
    });

    abrirFormModal({
      titulo: atual ? "Editar Retirada" : "Nova Retirada",
      rotuloSalvar: atual ? "Salvar" : "Registrar",
      campos: () =>
        el(
          "div",
          {},
          campo("Responsável", responsavel),
          campo("Data", data),
          campo("Valor", valor.element),
        ),
      salvar: (aoErro) => {
        const dados = {
          responsavelId: responsavel.value,
          data: data.value,
          // em branco = NaN: o domínio recusa, a fronteira não inventa 0
          valorCentavos: valor.centavos() ?? NaN,
        };
        const id = atual?.id ?? newId();
        const cmd = atual
          ? ({ tipo: "editar", id, dados } as const)
          : ({ tipo: "registrar", id, dados } as const);
        return store.executar({ agregado: "retirada", cmd }, undefined, aoErro);
      },
    });
  };

  render();
  return store.subscribe(render);
}

// ------------------------------------------------------------ responsáveis ---

function abrirGerenciarResponsaveis(store: Store, aoMudar?: () => void): void {
  openModal("Responsáveis", () => {
    const raiz = el("div", { class: "form-modal" });
    let editandoId: string | null = null;

    const linhaCampos = (nome: string, limite: number | null) => {
      const inpNome = inputTexto("Nome", nome);
      const inpLimite = inputMoeda({
        centavos: limite,
        placeholder: "Sem limite",
        ariaLabel: "Limite mensal",
      });
      return { nome: inpNome, limite: inpLimite };
    };

    const render = (): void => {
      clear(raiz);
      const lista = [...store.get().responsaveis].sort((a, b) =>
        a.nome.localeCompare(b.nome, "pt-BR"),
      );
      const ul = el("ul", { class: "lista-editavel" });
      if (lista.length === 0) {
        ul.append(el("li", { class: "muted" }, "Nenhum responsável."));
      }
      for (const r of lista) {
        if (editandoId === r.id) {
          const f = linhaCampos(r.nome, r.limiteMensalCentavos);
          const erro = el("span", { class: "hist-erro" });
          const ok = botao({ variante: "secundario", tamanho: "compacto" }, {}, "Salvar");
          const cancelar = botao({ variante: "secundario", tamanho: "compacto" }, {}, "Cancelar");
          ok.addEventListener("click", () => {
            criarAcaoComando(store, erro)(
              {
                agregado: "responsavel",
                cmd: {
                  tipo: "editar",
                  id: r.id,
                  dados: {
                    nome: f.nome.value,
                    limiteMensalCentavos: f.limite.centavos(),
                  },
                },
              },
              "Responsável salvo.",
              () => {
                editandoId = null;
                render();
                aoMudar?.();
              },
            );
          });
          cancelar.addEventListener("click", () => {
            editandoId = null;
            render();
          });
          ul.append(
            el("li", {}, el("div", { class: "hist-edit" }, f.nome, f.limite.element, ok, cancelar, erro)),
          );
          continue;
        }
        const editar = botao({ variante: "secundario", tamanho: "compacto" }, {}, "Editar");
        editar.addEventListener("click", () => {
          editandoId = r.id;
          render();
        });
        const alternar = botao(
          { variante: "secundario", tamanho: "compacto" },
          {},
          r.ativo ? "Inativar" : "Reativar",
        );
        alternar.addEventListener("click", () => {
          void store
            .executar(
              {
                agregado: "responsavel",
                cmd: { tipo: "definirAtivo", id: r.id, ativo: !r.ativo },
              },
              r.ativo ? "Responsável inativado." : "Responsável reativado.",
            )
            .then(
              () => {
                render();
                aoMudar?.();
              },
              () => render(),
            );
        });
        ul.append(
          el(
            "li",
            r.ativo ? {} : { class: "inativo" },
            el(
              "span",
              {},
              r.nome,
              el(
                "small",
                { class: "muted" },
                r.limiteMensalCentavos == null
                  ? " · sem limite"
                  : ` · limite ${formatReais(r.limiteMensalCentavos)}/mês`,
              ),
              r.ativo ? "" : " (inativo)",
            ),
            el("span", { class: "hist-acao" }, editar, alternar),
          ),
        );
      }

      const novo = linhaCampos("", null);
      const incluir = botao("secundario", {}, "Incluir");
      const erroNovo = el("p", { class: "form-erro", role: "alert" });
      const addUm = (): void => {
        criarAcaoComando(store, erroNovo)(
          {
            agregado: "responsavel",
            cmd: {
              tipo: "criar",
              id: newId(),
              dados: {
                nome: novo.nome.value,
                limiteMensalCentavos: novo.limite.centavos(),
              },
            },
          },
          "Responsável criado.",
          () => {
            render();
            aoMudar?.();
          },
        );
      };
      incluir.addEventListener("click", addUm);
      novo.nome.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          addUm();
        }
      });

      raiz.append(
        ul,
        el(
          "p",
          { class: "muted" },
          "O limite mensal é um aviso — nunca impede a retirada.",
        ),
        el("div", { class: "lista-add" }, novo.nome, novo.limite.element, incluir),
        erroNovo,
      );
    };

    render();
    return raiz;
  });
}

export type { Responsavel };
